import React, { useState, useEffect, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { ReactComponent as ChevronIcon } from '../../assets/accordion-arrow.svg'
import SquareCheckBox from './SquareCheckBox'
import { useTheme } from '../../context/theme'
import { on, off } from '../../hooks/useCustomEvent'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  user-select: none;
`

const Button = styled.button`
  background-color: ${props =>
    props.active === true
      ? props.theme.background.secondary
      : props.theme.background.secondary};
  cursor: pointer;
  padding: 1px 16px;
  display: flex;
  align-items: center;
  border: none;
  outline: none;
  /*transition: background-color 0.2s ease;*/

  &:hover {
    background-color: ${props => props.theme.accordion.highlight};
  }
`

const IconImage = styled.img`
  margin-right: 8px;
  filter: ${props => (props.scheme === 'dark' ? 'invert(100%)' : 'invert(0%)')};
`

const Title = styled.p`
  font-style: normal;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  color: ${props => props.theme.text.primary};
`

const Chevron = styled(ChevronIcon)`
  stroke: ${props => props.theme.text.primary};
  width: 10px;
  margin-left: auto;
  transition: transform 0.2s ease;
  transform: ${props =>
    props.active === 'true' ? 'rotate(0)' : 'rotate(-180deg)'};
`

const ContentContainer = styled.div`
  /*overflow: auto;*/
  transition: max-height 0.2s ease;
  overflow-y: hidden;
  background-color: ${props => props.theme.accordion.content};
`

const Accordion = ({
  title,
  children,
  icon,
  id,
  haveOptions,
  onSelect,
  selections,
  data,
  expand
}) => {
  const [active, setActive] = useState(false)
  const [selected, setSelected] = useState(false)
  const [setHeight, setHeightState] = useState('0px')
  const content = useRef(null)
  const { appearance } = useTheme()

  useEffect(() => {
    setSelected(false)
    if (!selections) return

    const check = selections.filter(e => e.catid === id)
    if (check && check.length > 0) setSelected(true)
    else setSelected(false)
  }, [selections, id])

  const toggleOpen = () => {
    setActive(true)
    const h = content.current.scrollHeight + 'px'
    setHeightState(h)
  }

  const toggleAccordion = useCallback(() => {
    const tog = !active
    setActive(tog)
    const h = tog === false ? '0px' : content.current.scrollHeight + 'px'
    setHeightState(h)
  }, [active])

  const toggleClose = () => {
    setActive(false)
    setHeightState('0px')
  }

  const handleSelect = e => {
    if (!data) return
    if (onSelect) onSelect(selected, data)
  }

  useEffect(() => {
    if (!expand) return
    toggleOpen()
  }, [expand])

  useEffect(() => {
    const handleJumpTo = e => {
      if (e.detail.type === 'categoryIds' && e.detail.catid === id) {
        toggleAccordion()
      } else if (e.detail.type === 'styles' && title === 'Style') {
        toggleAccordion()
      } else if (e.detail.type === 'storeIds' && title === 'Brand') {
        toggleAccordion()
      } else if (e.detail.type === 'colorDesigns' && title === 'Color') {
        toggleAccordion()
      } else {
        toggleClose()
      }
    }

    on('jumpTo', handleJumpTo)

    return () => {
      off('jumpTo', handleJumpTo)
    }
  }, [id, title, toggleAccordion])

  return (
    <Container>
      <Button active={active} onClick={toggleAccordion}>
        {onSelect && (
          <SquareCheckBox onClick={handleSelect} checked={selected} />
        )}
        {icon && <IconImage src={icon} scheme={appearance} />}
        <Title>{title}</Title>
        {haveOptions && <Chevron active={active.toString()} />}
      </Button>
      <ContentContainer ref={content} style={{ maxHeight: `${setHeight}` }}>
        {children}
      </ContentContainer>
    </Container>
  )
}

export default Accordion
